import React, { FC, useState, useEffect, useMemo } from 'react';
// common components
import { StyledMap, MapControl } from '@ems/client-design-system';
// functions
import { useMapRef, useMapApis, useMapProps, useMapConfig } from 'src/@app/functions/map';
import { useAuthSelectors } from 'src/@auth/reducers/authReducer';

import { flyTo, fitPointsInMap } from 'src/utils/mapHelpers';
// @ts-ignore
import sassVars from 'src/styles/vars.scss';
import { IMapProps } from 'src/@noiseEvents/interfaces';
import {
  useInAirPlayback,
  useNoiseMonitorPlayback,
  useStaticFlightDisplay,
  useStaticDbDisplay,
} from 'src/utils/playback';
import { NoisePopup } from 'src/components/NoisePopup';

export const MapContainer: FC<IMapProps> = ({
  inAirData,
  noiseMonitors,
  isPlaybackMode = false,
  currentTime = 0,
  noiseData,
  processedNoiseData,
  markedTime,
  pointData,
}) => {
  // map ref
  const [mapNode, mapRef] = useMapRef();
  // get map apis
  const mapApis: any = useMapApis(mapNode);
  // get map props from config
  const { viewportFromProps, ...mapProps } = useMapProps('2D');
  // viewport in state
  const [viewport, setViewport] = useState(viewportFromProps);
  // get mapbox config values required to add source and styles
  const mapBoxConfig = useMapConfig();

  // restrict map pan
  const onViewportChange = viewport => {
    if (
      Math.abs(viewport.latitude - viewportFromProps.latitude) < mapBoxConfig.limitLatitude &&
      Math.abs(viewport.longitude - viewportFromProps.longitude) < mapBoxConfig.limitLongitude
    ) {
      setViewport(viewport);
    }
  };

  // resets map view
  const resetView = () => {
    if (mapApis) {
      const resetViewport = Object.assign({}, viewportFromProps, { zoom: viewport.zoom });
      flyTo(mapApis, resetViewport).then(() => {
        setViewport(Object.assign({}, viewport, resetViewport));
      });
    }
  };

  const accessToken = useAuthSelectors().getAccessToken();

  const highlightId = noiseData.length ? noiseData[0].operationId : -1;
  const highlightIds: number[] = useMemo(() => {
    const ids: number[] = [];
    noiseData.map(data => {
      ids.push(data.operationId);
    });
    return ids;
  }, [noiseData]);
  const selectedMonitor = useNoiseMonitorPlayback({
    mapApis,
    noiseMonitors,
    noiseData,
    processedNoiseData,
    currentTime,
    isPlaybackMode,
    showNMTDefault: false,
  });
  useStaticFlightDisplay(mapApis, pointData, isPlaybackMode, true, highlightIds, markedTime);
  const staticMonitors = useStaticDbDisplay(mapApis, noiseMonitors, noiseData, isPlaybackMode);
  useInAirPlayback(mapApis, inAirData, currentTime, isPlaybackMode, highlightId);

  // make map zoom to fit the points selected
  useEffect(() => {
    if (mapApis) {
      const selectedMonitorCoords: any = [];
      staticMonitors.map((monitor: any) => {
        selectedMonitorCoords.push({
          longitude: monitor.geometry.coordinates[0],
          latitude: monitor.geometry.coordinates[1],
        });
      });

      if (!isPlaybackMode) {
        fitPointsInMap(mapApis, viewport, setViewport, selectedMonitorCoords);
      }
    }
  }, [mapApis, staticMonitors]);

  useEffect(() => {
    if (mapApis) {
      // Reorder layers if needed
      if (mapApis.getLayer('noiseMonitors')) {
        mapApis.moveLayer('noiseMonitors');
      }
      if (mapApis.getLayer('static-monitors')) {
        mapApis.moveLayer('static-monitors');
      }
      if (mapApis.getLayer('selectedMonitors')) {
        mapApis.moveLayer('selectedMonitors');
      }
      if (mapApis.getLayer('static-points')) {
        mapApis.moveLayer('static-points');
      }
      if (mapApis.getLayer('static-lines')) {
        mapApis.moveLayer('static-lines');
      }
      if (mapApis.getLayer('lines')) {
        mapApis.moveLayer('lines');
      }
      if (mapApis.getLayer('points')) {
        mapApis.moveLayer('points');
      }
    }
  }, [inAirData, mapApis, noiseData]);

  const noisePopups: JSX.Element[] = [];
  if (mapApis && (selectedMonitor.length > 0 || staticMonitors.length > 0)) {
    const mapFn = (monitor: any) => {
      noisePopups.push(
        <NoisePopup
          key={`popup_${monitor.properties.id}`}
          zoomLevel={mapApis.getZoom()}
          monitorData={monitor}
        />
      );
    };
    if (isPlaybackMode) {
      selectedMonitor.map(mapFn);
    } else {
      staticMonitors.map(mapFn);
    }
  }

  return (
    <StyledMap
      viewport={viewport}
      onViewportChange={viewport => {
        viewport.maxPitch = 0;
        onViewportChange(viewport);
      }}
      {...mapProps}
      ref={mapRef}
      transformRequest={
        mapBoxConfig && mapBoxConfig.transformRequest && mapBoxConfig.transformRequest(accessToken)
      }>
      {noisePopups}
      <MapControl resetView={resetView} />
    </StyledMap>
  );
};
